import { useEffect } from 'react';

import { useForm, FormProvider } from 'react-hook-form';

import { styled } from '@mui/material';

import {
  Typography,
  Form,
  Modal,
  TextField,
  FormErrorLabel,
} from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { ClassValidatorResolver } from '@app-velocity/validators';
import { MessageBus } from '@app-velocity/message-bus';

import { useUserData } from '@app-velocity/libs/auth';

import { ModalWindowControls } from '../ModalWindowControls';
import { useUpdateUserData } from '../../../../hooks';

import { UserEditEmailDto } from '../../../../dto/UserEditEmailDto';

const messageBus = new MessageBus('users');

const StyledTypography = styled(Typography)({
  fontSize: 'var(--font-size-medium)',
});

export const EditEmailModalContent = () => {
  const { userId } = useUserData();
  const { t } = useI18n();

  const { update, isLoading, error, refreshError, isUpdateSuccess } =
    useUpdateUserData();

  const methods = useForm<UserEditEmailDto>({
    mode: 'onChange',
    resolver: ClassValidatorResolver(UserEditEmailDto),
  });

  const { handleSubmit, control } = methods;

  const onSubmit = (data: UserEditEmailDto) => {
    if (!userId) return;

    update({
      updateEmail: {
        email: data.email,
        password: data.password,
      },
    });
  };

  useEffect(() => {
    if (!isUpdateSuccess) return;

    messageBus.publish('modal:user-settings:open', { tab: 'profile' });
  }, [isUpdateSuccess]);

  return (
    <Modal data-testid="change-email-modal">
      <Modal.Header title="users:settings.change_email" />
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Content>
            <StyledTypography>
              {t('users:settings.change_email_instructions')}
            </StyledTypography>
            <Form.FieldContainer>
              <Form.Label>{t('users:settings.new_email')}</Form.Label>
              <TextField
                control={control}
                name="email"
                placeholder={t('users:settings.change_email_placeholder')}
                fullWidth={true}
              />
            </Form.FieldContainer>
            <Form.FieldContainer>
              <Form.Label>{t('users:password')}</Form.Label>
              <TextField
                control={control}
                name="password"
                type="password"
                placeholder={t('users:password')}
                fullWidth={true}
              />
            </Form.FieldContainer>
          </Modal.Content>
          <Modal.Controls>
            {error || refreshError ? (
              <FormErrorLabel
                error={new Error(t('users:settings.error_saving_user_data'))}
              />
            ) : null}
            <ModalWindowControls isLoading={isLoading} />
          </Modal.Controls>
        </Form>
      </FormProvider>
    </Modal>
  );
};
